import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Cpu, Loader2, Wrench } from 'lucide-react';
import { inputClass } from './caseConstants';

// The simulator bay this case concerns, and the switch that takes it off the
// floor. Setting maintenance writes the flags the booking engine reads, so the
// bay disappears from the booking and walk-in pickers until it is back.
export default function CaseLinkedBayCard({ caseData, rigs = [], onDone }) {
  const [selected, setSelected] = useState(caseData.rig_id || '');
  const [note, setNote] = useState('');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const rig = rigs.find((r) => r.key === selected) || null;
  const on = !!rig?.maintenance;

  const toggle = async (nextOn) => {
    const key = selected;
    if (!key) {
      setError('Choose a bay first.');
      return;
    }
    if (nextOn && !note.trim() && !rig?.maintenance_note) {
      setError('Add a maintenance note so the team knows what is being worked on.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await base44.functions.invoke('caseBayMaintenance', {
        caseId: caseData.id,
        rigKey: key,
        maintenance: nextOn,
        note: nextOn ? note : undefined,
        until: nextOn ? until : undefined,
      });
      if (res.data?.error) throw new Error(res.data.error);
      setNote('');
      setUntil('');
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not update the bay.');
    }
    setBusy(false);
  };

  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Cpu className="w-3.5 h-3.5 text-primary" />
          <p className="text-white text-[10px] font-bold tracking-widest">LINKED SIMULATOR BAY</p>
        </div>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
      </div>

      <div className="px-4 py-3 space-y-3">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className={inputClass}
        >
          <option value="">No bay selected</option>
          {rigs.map((r) => (
            <option key={r.key} value={r.key}>
              {r.name}
              {r.maintenance ? ' — under maintenance' : ''}
            </option>
          ))}
        </select>

        {rig ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-white/50 text-xs truncate">{rig.name}</span>
              <span
                className={`text-[9px] font-bold tracking-widest px-2 py-1 border shrink-0 ${
                  on
                    ? 'border-amber-400/40 text-amber-300 bg-amber-400/10'
                    : 'border-green-400/40 text-green-300 bg-green-400/10'
                }`}
              >
                {on ? 'UNDER MAINTENANCE' : 'IN SERVICE'}
              </span>
            </div>

            {on && (rig.maintenance_note || rig.maintenance_until) && (
              <div className="border border-amber-400/20 bg-amber-400/5 px-3 py-2">
                {rig.maintenance_note && (
                  <p className="text-amber-200/80 text-[11px]">{rig.maintenance_note}</p>
                )}
                {rig.maintenance_until && (
                  <p className="text-amber-400/60 text-[10px] mt-1">
                    Back in service {new Date(rig.maintenance_until).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            )}

            {!on && (
              <>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What is being worked on, and by whom?"
                  className={inputClass}
                />
                <input
                  type="date"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                  className={inputClass}
                />
              </>
            )}

            <button
              onClick={() => toggle(!on)}
              disabled={busy}
              className={`w-full flex items-center justify-center gap-2 border text-[10px] font-bold tracking-widest px-3 py-2.5 transition-colors disabled:opacity-50 ${
                on
                  ? 'border-green-400/40 hover:bg-green-400/10 text-green-300'
                  : 'border-amber-400/40 hover:bg-amber-400/10 text-amber-300'
              }`}
            >
              <Wrench className="w-3.5 h-3.5" />
              {on ? 'RETURN BAY TO SERVICE' : 'SET BAY UNDER MAINTENANCE'}
            </button>
            <p className="text-white/25 text-[10px]">
              A bay under maintenance is withheld from the booking engine and the walk-in POS.
            </p>
          </>
        ) : (
          <p className="text-white/30 text-xs py-2">
            Choose a bay to see its service state and take it off the floor.
          </p>
        )}

        {error && <p className="text-red-400 text-[11px]">{error}</p>}
      </div>
    </div>
  );
}